/**
 * Telefonnummern im KMVS-Format „+49 221 123456-78" —
 * „+Ländervorwahl Ortsvorwahl Rufnummer-Durchwahl".
 *
 * Reine Funktionen (kein React, kein DOM), damit dieselbe Logik im CRM
 * (apps/web), im Kundenportal (forms-react) und bei der Normalisierung der
 * Analyse-Antworten gilt. Dependency-frei wie der Rest des Pakets.
 *
 * Die Regeln (Vorgabe des Fachbereichs, 09/2026):
 *
 * - Das Feld beginnt IMMER mit „+". Wer etwas anderes als „+" tippt, bekommt
 *   „+49 " vorangestellt; eine führende 0 ist die Amtsnull und wird durch
 *   „+49 " ersetzt („0221…" → „+49 221…"), „00" ist die internationale
 *   Verkehrsausscheidung („0049…" → „+49…").
 * - Die Ländervorwahl ist editierbar; das Feld darf ganz leer werden.
 * - Deutsche Nummern werden hinter der Ortsnetzkennzahl (Liste der BNetzA,
 *   `telefonVorwahlenDe.ts`) bzw. der Mobilfunk-/Sondervorwahl getrennt.
 *   Für andere Länder gibt es keine verlässliche Vorwahltabelle: dort bleibt
 *   es bei „+CC Rest".
 * - „-" (Durchwahl) nur bei geschäftlichen Nummern und nie als erstes Zeichen
 *   der Rufnummer. Zusätzlich setzt {@link TelefonOptionen.muster} die
 *   Durchwahl selbst, wenn die Nummer erkennbar mit der Zentrale beginnt.
 * - Die Durchwahl hat höchstens {@link MAX_DURCHWAHL} (4) Stellen (T-60,
 *   10/2026): eine fünfte Ziffer wird beim Tippen nicht angenommen; eingefügte
 *   oder bestehende längere Durchwahlen werden NICHT gekürzt (sonst stünde
 *   eine falsche Nummer in der Akte), sondern von {@link pruefeTelefon} als
 *   Fehler gemeldet.
 */
import { KNOWN_DIAL_CODES } from "./phoneCountries.js";
import { ORTSNETZ_TRIE } from "./telefonVorwahlenDe.js";
/* ------------------------------------------------------------------ *
 * Vorwahlen
 * ------------------------------------------------------------------ */
/**
 * Ländervorwahlen, die in der ISO-Tabelle fehlen, weil sie zu keinem Land
 * gehören (Kosovo, internationale Dienste, Satellit). Ohne sie würde „+383 …"
 * als unbekannt ungetrennt stehen bleiben.
 */
const WEITERE_LAENDERVORWAHLEN = ["383", "800", "808", "870", "878", "881", "882", "883", "888", "979", "991", "247"];
const LAENDERVORWAHLEN = new Set([...KNOWN_DIAL_CODES, ...WEITERE_LAENDERVORWAHLEN]);
let ortsnetzWurzel;
/** Liest {@link ORTSNETZ_TRIE} einmalig in einen Baum (≈ 5.200 Knoten). */
function ortsnetze() {
    if (ortsnetzWurzel)
        return ortsnetzWurzel;
    const s = ORTSNETZ_TRIE;
    let i = 0;
    const lies = () => {
        const kinder = {};
        while (i < s.length && s[i] !== ")") {
            const ziffer = s[i++];
            const knoten = { ende: true, kinder: {} };
            if (s[i] === "!" || s[i] === "(") {
                knoten.ende = s[i] === "!";
                if (s[i] === "!")
                    i++;
                i++; // "("
                knoten.kinder = lies();
                i++; // ")"
            }
            kinder[ziffer] = knoten;
        }
        return kinder;
    };
    ortsnetzWurzel = { ende: false, kinder: lies() };
    return ortsnetzWurzel;
}
/** Alle Ortsnetzkennzahlen als Liste — für Tests und Diagnose. */
export function alleOrtsnetzkennzahlen() {
    const out = [];
    const lauf = (k, pfad) => {
        if (k.ende && pfad)
            out.push(pfad);
        for (const [z, kind] of Object.entries(k.kinder))
            lauf(kind, pfad + z);
    };
    lauf(ortsnetze(), "");
    return out;
}
/**
 * Länge der deutschen Vorwahl (ohne 0) am Anfang von `national`, oder
 * `undefined`, solange sie nicht (eindeutig) feststeht — dann bleibt die
 * Nummer ungetrennt, statt eine Trennung zu raten.
 *
 * - Mobilfunk: 015x ist VIERstellig („1512", „1520", „1590"), 016x und 017x
 *   sind DREIstellig („160", „171", „176").
 * - Sonderrufnummern: 0800/0700/0900 (3), 0180x und 0137x (4), 0118xx (5),
 *   0116xxx (6), 0115 (3), 032 (2).
 * - Sonst: längste passende Ortsnetzkennzahl der BNetzA.
 */
export function deutscheVorwahlLaenge(national) {
    const n = national.replace(/\D/g, "");
    if (!n)
        return undefined;
    const min = (len) => (n.length >= len ? len : undefined);
    if (n[0] === "1") {
        const p2 = n.slice(0, 2);
        const p3 = n.slice(0, 3);
        if (p2 === "15")
            return min(4);
        if (p2 === "16" || p2 === "17")
            return min(3);
        if (p3 === "180" || p3 === "137" || p3 === "138")
            return min(4);
        if (p3 === "118")
            return min(5);
        if (p3 === "116")
            return min(6);
        if (p3 === "115")
            return min(3);
        return undefined;
    }
    const p3 = n.slice(0, 3);
    if (p3 === "700" || p3 === "800" || p3 === "900")
        return 3;
    if (n.slice(0, 2) === "32")
        return 2;
    let knoten = ortsnetze();
    let laenge;
    for (let i = 0; i < n.length && knoten; i++) {
        knoten = knoten.kinder[n[i]];
        if (knoten?.ende)
            laenge = i + 1;
    }
    return laenge;
}
const BUCHSTABEN = /[A-Za-zÄÖÜäöüß]/;
const STRICHE = /[-‐-―−]/;
/** Enthält der Text Buchstaben — also Freitext statt Rufnummer? */
export const istTelefonFreitext = (text) => BUCHSTABEN.test(text);
const syn = (text) => text.split("").map((z) => ({ z, q: -1 }));
function zerlegeTokens(tokens, opts, tiefe = 0) {
    const leer = { zeichen: [], cc: "", national: "", ausNull: false };
    if (!tokens.length)
        return leer;
    let plus;
    let rest = tokens;
    let ccSynthetisch = false;
    let ausNull = false;
    if (rest[0].z === "+") {
        plus = rest[0];
        rest = rest.slice(1);
    }
    // Striche vor der ersten Ziffer bedeuten nichts.
    const ersteZiffer = rest.findIndex((t) => t.z !== "-");
    rest = ersteZiffer < 0 ? [] : rest.slice(ersteZiffer);
    if (!plus) {
        if (rest[0]?.z === "0" && rest[1]?.z === "0") {
            // 00 = internationale Verkehrsausscheidungsziffer → „+".
            plus = { z: "+", q: rest[0].q };
            rest = rest.slice(2);
        }
        else if (rest[0]?.z === "0") {
            // Amtsnull → +49.
            rest = rest.slice(1);
            ccSynthetisch = true;
            ausNull = true;
        }
        else {
            ccSynthetisch = true;
        }
    }
    else if (rest[0]?.z === "0" && tiefe < 2) {
        // „+0…" ist keine Ländervorwahl: wie ohne „+" lesen (Amtsnull bzw. 00).
        return zerlegeTokens(rest, opts, tiefe + 1);
    }
    const ziffern = rest.filter((t) => t.z !== "-");
    let cc = "";
    let ccTokens = [];
    if (ccSynthetisch) {
        cc = "49";
    }
    else {
        const kopf = ziffern.slice(0, 3).map((t) => t.z).join("");
        for (let len = 1; len <= kopf.length; len++) {
            if (LAENDERVORWAHLEN.has(kopf.slice(0, len))) {
                cc = kopf.slice(0, len);
                break;
            }
        }
        if (!cc) {
            // Noch unvollständig („+4") oder unbekannt: „+" und die Ziffern, ungetrennt.
            return {
                zeichen: [plus ? { z: "+", q: plus.q } : { z: "+", q: -1 }, ...ziffern.map((t) => ({ z: t.z, q: t.q }))],
                cc: "",
                national: "",
                ausNull: false,
            };
        }
        ccTokens = ziffern.slice(0, cc.length);
    }
    // Alles hinter der Ländervorwahl (mit Strichen, für die Durchwahl).
    const letzteCc = ccTokens.length ? ccTokens[ccTokens.length - 1] : undefined;
    let nat = letzteCc ? rest.slice(rest.indexOf(letzteCc) + 1) : rest;
    const natZiffern = () => nat.filter((t) => t.z !== "-");
    // Amtsnull hinter der Ländervorwahl („+49 0221", „+49 (0) 221").
    const nz = natZiffern();
    if (nz[0]?.z === "0") {
        if (nz[1]?.z === "0" && tiefe < 2) {
            // „+49 00…" — da hat jemand die internationale Schreibweise dahinter
            // getippt oder eingefügt: neu lesen, ab dem „+".
            const ab = nat.indexOf(nz[1]) + 1;
            return zerlegeTokens([{ z: "+", q: nz[0].q }, ...nat.slice(ab)], opts, tiefe + 1);
        }
        if (nz.length > 1) {
            nat = nat.slice(nat.indexOf(nz[0]) + 1);
        }
        // Eine einzelne 0 bleibt stehen, bis die nächste Ziffer entscheidet.
    }
    const national = natZiffern().map((t) => t.z).join("");
    const vorwahl = cc === "49" && national !== "0" ? deutscheVorwahlLaenge(national) : undefined;
    // Durchwahl: erster Strich, vor dem mindestens eine Ziffer der Rufnummer
    // (hinter der Ortsvorwahl) steht.
    let durchwahlAb;
    let strichToken;
    if (opts.geschaeftlich) {
        let zaehler = 0;
        for (const t of nat) {
            if (t.z === "-") {
                if (zaehler > (vorwahl ?? 0) && zaehler >= 1) {
                    durchwahlAb = zaehler;
                    strichToken = t;
                    break;
                }
            }
            else
                zaehler++;
        }
    }
    const zeichen = [];
    zeichen.push(plus ? { z: "+", q: plus.q } : { z: "+", q: -1 });
    if (ccSynthetisch)
        zeichen.push(...syn("49"));
    else
        for (const t of ccTokens)
            zeichen.push({ z: t.z, q: t.q });
    const natOhneStrich = natZiffern();
    if (natOhneStrich.length || ccSynthetisch)
        zeichen.push({ z: " ", q: -1 });
    natOhneStrich.forEach((t, i) => {
        if (vorwahl && i === vorwahl)
            zeichen.push({ z: " ", q: -1 });
        if (durchwahlAb !== undefined && i === durchwahlAb)
            zeichen.push({ z: "-", q: strichToken.q });
        zeichen.push({ z: t.z, q: t.q });
    });
    if (durchwahlAb !== undefined && durchwahlAb === natOhneStrich.length) {
        zeichen.push({ z: "-", q: strichToken.q });
    }
    return {
        zeichen,
        cc,
        national,
        vorwahl,
        durchwahlAb,
        ausNull: ausNull && ccSynthetisch && national === "",
    };
}
function tokenisiere(roh) {
    const tokens = [];
    for (let i = 0; i < roh.length; i++) {
        const z = roh[i];
        if (z >= "0" && z <= "9")
            tokens.push({ z, q: i });
        else if (z === "+" && tokens.length === 0)
            tokens.push({ z, q: i });
        else if (STRICHE.test(z))
            tokens.push({ z: "-", q: i });
    }
    return tokens;
}
/** Endet der Rohtext mit einem Trenner (Leerzeichen, „/", „." …) hinter Ziffern? */
const endetMitTrenner = (roh) => {
    // Ein nicht angenommener Strich am Ende zählt nicht — sonst würde aus einem
    // privaten „…12345-“ ein „…12345 “.
    const r = roh.replace(/[-\u2010-\u2015\u2212]+$/, "");
    return /\d[\s\u00A0\u202F/.()]+$/.test(r);
};
const zentralen = (z) => (Array.isArray(z) ? z : [z]).filter((x) => typeof x === "string" && x.trim() !== "");
/** Höchstens so viele Stellen hat eine Durchwahl (Vorgabe Fachbereich 10/2026, T-60). */
export const MAX_DURCHWAHL = 4;
/**
 * Durchwahl aus dem Muster „beginnt mit der Zentrale" ableiten. Liefert den
 * Index in `national`, an dem die Durchwahl beginnt, oder `undefined`.
 */
function durchwahlAusMuster(z, opts) {
    if (!opts.geschaeftlich || !opts.muster || z.durchwahlAb !== undefined || !z.cc)
        return undefined;
    for (const zentraleRoh of zentralen(opts.zentrale)) {
        if (istTelefonFreitext(zentraleRoh))
            continue;
        const zen = zerlegeTokens(tokenisiere(zentraleRoh), { geschaeftlich: true });
        if (zen.cc !== z.cc || !zen.national)
            continue;
        const basis = zen.durchwahlAb !== undefined ? zen.national.slice(0, zen.durchwahlAb) : zen.national;
        // Die Basis muss über die Ortsvorwahl hinausgehen — sonst wäre jede Nummer
        // im selben Ortsnetz eine „Durchwahl".
        if (basis.length < (z.vorwahl ?? 0) + 2)
            continue;
        const extra = z.national.length - basis.length;
        if (extra >= 1 && extra <= MAX_DURCHWAHL && z.national.startsWith(basis))
            return basis.length;
    }
    return undefined;
}
/**
 * Zentrale(n) aus mehreren geschäftlichen Nummern DERSELBEN Person (oder
 * Firma) ableiten — für Fälle, in denen keine Firmen-Zentrale bekannt ist.
 *
 * - Eine Nummer mit ausdrücklicher Durchwahl („+49 221 12345-67") nennt ihre
 *   Zentrale selbst: „+49 221 12345".
 * - Zwei deutsche Festnetznummern, die sich nur in den letzten Stellen
 *   unterscheiden („0221 1234567" und „0221 1234589"), haben den gemeinsamen
 *   Anfang als Zentrale („+49 221 12345"). Ist die eine der Anfang der anderen
 *   („0221 12345" und „0221 1234567"), ist die kürzere die Zentrale.
 *
 * Bewusst vorsichtig: nur +49 mit bekannter Ortsnetzkennzahl (für andere
 * Länder wissen wir nicht, wo die Rufnummer beginnt), keine Mobilfunk- und
 * Sondernummern (zwei Diensthandys mit ähnlicher Nummer sind keine Anlage),
 * mindestens drei Stellen Rufnummer vor der Durchwahl und höchstens
 * {@link MAX_DURCHWAHL} Stellen Durchwahl. Bei zwei gleich
 * beginnenden Nummern gilt der längste gemeinsame Anfang — „1234567"/„1234589"
 * wird „12345-67"/„12345-89", nicht „1234-567".
 *
 * Das Ergebnis geht als {@link TelefonOptionen.zentrale} in die Formatierung;
 * die Durchwahl setzt dann wie immer nur das Muster (Einfügen/Verlassen).
 */
export function zentraleAusNummern(nummern) {
    const teile = nummern
        .filter((n) => typeof n === "string" && n.trim() !== "" && !istTelefonFreitext(n))
        .map((n) => zerlegeTokens(tokenisiere(n), { geschaeftlich: true }))
        .filter((z) => z.cc && z.national);
    const out = [];
    const gesehen = new Set();
    const merken = (cc, basis) => {
        const schluessel = `${cc}|${basis}`;
        if (gesehen.has(schluessel))
            return;
        gesehen.add(schluessel);
        out.push(formatiereTelefon(`+${cc}${basis}`));
    };
    // 1. Ausdrückliche Durchwahl: der Teil davor ist die Zentrale.
    for (const z of teile) {
        if (z.durchwahlAb === undefined)
            continue;
        const basis = z.national.slice(0, z.durchwahlAb);
        if (basis.length >= (z.vorwahl ?? 0) + 2)
            merken(z.cc, basis);
    }
    // 2. Gemeinsamer Anfang zweier Nummern ohne Durchwahl.
    const festnetz = teile.filter((z) => z.durchwahlAb === undefined && z.cc === "49" && !!z.vorwahl && z.national[0] !== "1");
    for (let i = 0; i < festnetz.length; i++) {
        for (let j = i + 1; j < festnetz.length; j++) {
            const a = festnetz[i];
            const b = festnetz[j];
            const vorwahl = a.vorwahl ?? 0;
            if (vorwahl !== b.vorwahl || a.national === b.national)
                continue;
            let p = 0;
            while (p < a.national.length && p < b.national.length && a.national[p] === b.national[p])
                p++;
            if (p < vorwahl + 3)
                continue;
            if (a.national.length - p > MAX_DURCHWAHL || b.national.length - p > MAX_DURCHWAHL)
                continue;
            merken("49", a.national.slice(0, p));
        }
    }
    return out;
}
/** Zerlegung → Zeichen, inkl. Durchwahl aus dem Muster. */
function zeichenMitMuster(z, opts) {
    const ab = durchwahlAusMuster(z, opts);
    if (ab === undefined)
        return z.zeichen;
    // Den Strich hinter die ab-te Ziffer der Rufnummer setzen.
    const out = [];
    let ziffernGesehen = 0;
    let inNational = false;
    let ccZiffern = 0;
    for (const c of z.zeichen) {
        if (!inNational) {
            out.push(c);
            if (/\d/.test(c.z))
                ccZiffern++;
            if (ccZiffern === z.cc.length && c.z === " ")
                inNational = true;
            continue;
        }
        if (/\d/.test(c.z)) {
            if (ziffernGesehen === ab)
                out.push({ z: "-", q: -1 });
            ziffernGesehen++;
        }
        out.push(c);
    }
    return out;
}
/**
 * Rohtext ins Format bringen und die Cursorposition mitführen.
 * Buchstaben werden ignoriert (sie gehören in keine Rufnummer).
 */
export function formatiereTelefonRoh(roh, cursor, opts = {}) {
    const tokens = tokenisiere(roh);
    if (!tokens.some((t) => t.z !== "-"))
        return { wert: "", cursor: 0, ausNull: false };
    const zerlegung = zerlegeTokens(tokens, opts);
    let zeichen = zeichenMitMuster(zerlegung, opts);
    if (!zerlegung.cc && roh.trimStart().startsWith("+")) {
        // Ländervorwahl (noch) unbekannt — etwa während man sie ändert: die
        // Leerzeichen des Nutzers bleiben stehen, sonst klebte die Rufnummer an
        // der halben Vorwahl („+4221…“ statt „+4 221…“).
        zeichen = [];
        for (let i = 0; i < roh.length; i++) {
            const z = roh[i];
            if (z === "+" && !zeichen.length)
                zeichen.push({ z, q: i });
            else if (/\d/.test(z) && zeichen.length)
                zeichen.push({ z, q: i });
            else if (/\s/.test(z) && zeichen.length > 1 && zeichen[zeichen.length - 1].z !== " ")
                zeichen.push({ z: " ", q: i });
        }
        while (zeichen.length > 1 && zeichen[zeichen.length - 1].z === " ")
            zeichen.pop();
    }
    let wert = zeichen.map((c) => c.z).join("");
    if (endetMitTrenner(roh) && /\d$/.test(wert)) {
        wert += " ";
        zeichen.push({ z: " ", q: -1 });
    }
    // Cursor: hinter das letzte Ausgabezeichen, das aus dem Rohtext VOR dem
    // Cursor stammt. Gibt es keins, hinter die von uns ergänzten Zeichen am
    // Anfang („+49 ").
    let pos = -1;
    zeichen.forEach((c, i) => {
        if (c.q >= 0 && c.q < cursor)
            pos = i;
    });
    let neu;
    if (pos >= 0) {
        neu = pos + 1;
    }
    else {
        neu = 0;
        while (neu < zeichen.length && zeichen[neu].q < 0)
            neu++;
    }
    // Steht der Cursor am Ende des Rohtexts, gehört er ans Ende.
    if (cursor >= roh.length)
        neu = wert.length;
    return { wert, cursor: Math.min(neu, wert.length), ausNull: zerlegung.ausNull };
}
/**
 * Anzeigeform eines (gespeicherten oder eingefügten) Werts. Freitext mit
 * Buchstaben bleibt unverändert — er darf beim bloßen Anzeigen nicht
 * verschwinden.
 */
export function formatiereTelefon(wert, opts = {}) {
    const roh = String(wert ?? "").trim();
    if (!roh || istTelefonFreitext(roh))
        return roh;
    return telefonSpeicherwert(formatiereTelefonRoh(roh, roh.length, opts).wert);
}
/**
 * Was gespeichert wird: die Anzeige ohne angefangene Reste. Ein Feld, in dem
 * nur die Ländervorwahl steht („+49 "), ist leer.
 */
export function telefonSpeicherwert(anzeige) {
    const s = String(anzeige ?? "").trim();
    if (!s || istTelefonFreitext(s))
        return s;
    const ohneStrich = s.replace(/[\s-]+$/, "");
    const m = ohneStrich.match(/^\+(\d{1,3})(?:\s+(.*))?$/);
    if (m && !(m[2] ?? "").replace(/^0$/, "").replace(/\D/g, ""))
        return "";
    if (/^\+\d{0,3}$/.test(ohneStrich))
        return "";
    return ohneStrich;
}
const signifikant = (s) => s.replace(/[^\d+\-]/g, "");
/** Stellen hinter dem Durchwahl-Strich eines (formatierten) Werts, 0 ohne Durchwahl. */
function durchwahlLaenge(wert) {
    if (!wert || istTelefonFreitext(wert))
        return 0;
    const z = zerlegeTokens(tokenisiere(wert), { geschaeftlich: true });
    return z.durchwahlAb !== undefined ? z.national.length - z.durchwahlAb : 0;
}
/**
 * Ein Tastendruck im Telefonfeld: `vorher` ist der bisherige Feldinhalt, `roh`
 * der Inhalt, den der Browser daraus gemacht hat, `cursor` dessen
 * Cursorposition.
 */
export function telefonEingabe(vorher, roh, cursor, opts = {}) {
    const alt = vorher.wert ?? "";
    // Für die Durchwahl-Sperre unten: Eingabe so, wie sie vom Browser kam.
    const rohLaenge = roh.length;
    const rohCursor = cursor;
    // Bestands-Freitext („bitte über Frau Meier") bleibt Freitext, bis die
    // Buchstaben weg sind — sonst verschwände er beim ersten Tastendruck.
    if (istTelefonFreitext(roh) && istTelefonFreitext(alt)) {
        return { wert: roh, cursor, ausNull: false };
    }
    // „0" → „+49 ", dann noch eine „0": das war „00", also „+".
    if (vorher.ausNull && alt === "+49 " && signifikant(roh) === "+490") {
        return { wert: "+", cursor: 1, ausNull: false };
    }
    // Ein Trenner (Leerzeichen) wurde gelöscht, die Ziffern sind dieselben:
    // Ohne Sonderbehandlung setzte die Formatierung ihn sofort wieder und die
    // Löschtaste täte scheinbar nichts. Stattdessen die Ziffer daneben löschen.
    if (roh.length === alt.length - 1 &&
        cursor < roh.length &&
        signifikant(roh) === signifikant(alt) &&
        signifikant(roh) !== "") {
        if (opts.richtung === "vor") {
            let i = cursor;
            while (i < roh.length && !/[\d+\-]/.test(roh[i]))
                i++;
            if (i < roh.length)
                roh = roh.slice(0, i) + roh.slice(i + 1);
        }
        else {
            let i = cursor - 1;
            while (i >= 0 && !/[\d+\-]/.test(roh[i]))
                i--;
            if (i >= 0) {
                roh = roh.slice(0, i) + roh.slice(i + 1);
                cursor = i;
            }
        }
    }
    // Das „+" am Anfang wurde gelöscht bzw. die Ländervorwahl überschrieben,
    // der Rest der Nummer steht noch: das „+" bleibt, sonst würde aus
    // „49 221 …" ein „+49 49 221 …".
    const rohGetrimmt = roh.replace(/^\s+/, "");
    if (alt.startsWith("+") && rohGetrimmt && !rohGetrimmt.startsWith("+") && rohGetrimmt.includes("+")) {
        // Vor dem „+" getippt: das „+" bleibt vorn, die Ziffer gehört dahinter.
        const weg = roh.length - rohGetrimmt.length;
        roh = "+" + rohGetrimmt.replace("+", "");
        cursor = Math.max(1, cursor - weg + 1);
    }
    else if (alt.startsWith("+") && rohGetrimmt && !rohGetrimmt.startsWith("+") && /^[1-9]/.test(rohGetrimmt)) {
        let prefix = 0;
        while (prefix < alt.length && prefix < roh.length && alt[prefix] === roh[prefix])
            prefix++;
        let suffix = 0;
        while (suffix < alt.length - prefix &&
            suffix < roh.length - prefix &&
            alt[alt.length - 1 - suffix] === roh[roh.length - 1 - suffix])
            suffix++;
        if (prefix === 0 && suffix > 0) {
            const weg = roh.length - rohGetrimmt.length;
            roh = "+" + rohGetrimmt;
            cursor = Math.max(1, cursor - weg + 1);
        }
    }
    const ergebnis = formatiereTelefonRoh(roh, cursor, opts);
    // Durchwahl höchstens MAX_DURCHWAHL Stellen (T-60): ein Tastendruck, der sie
    // darüber hinaus wachsen ließe, wird verworfen — Wert und Cursor bleiben wie
    // vorher. Kürzer werden (Löschen) und die Hauptnummer bearbeiten bleibt auch
    // bei Bestandswerten mit zu langer Durchwahl erlaubt.
    if (opts.geschaeftlich) {
        const neu = durchwahlLaenge(ergebnis.wert);
        if (neu > MAX_DURCHWAHL && neu > durchwahlLaenge(alt)) {
            return {
                wert: alt,
                cursor: Math.max(0, Math.min(alt.length, rohCursor - (rohLaenge - alt.length))),
                ausNull: !!vorher.ausNull,
            };
        }
    }
    return ergebnis;
}
/**
 * Text einfügen. Eine vollständige Nummer („+49 …", „0049 …", „0221/…",
 * „(0221) …") ersetzt ein leeres bzw. nur mit der Ländervorwahl belegtes
 * Feld; sonst wird an der Cursorstelle eingesetzt. In beiden Fällen wird das
 * Durchwahl-Muster angewendet.
 */
export function telefonEinfuegen(vorher, text, auswahl, opts = {}) {
    const alt = vorher.wert ?? "";
    const sauber = String(text ?? "").replace(/^[^\d+(]+/, "").trim();
    const start = Math.max(0, Math.min(auswahl.start, alt.length));
    const ende = Math.max(start, Math.min(auswahl.ende, alt.length));
    const rest = alt.slice(0, start) + alt.slice(ende);
    const altOhneNummer = telefonSpeicherwert(rest) === "";
    const ganzeNummer = /^(\+|0|\()/.test(sauber);
    const mitMuster = { ...opts, muster: true };
    if (ganzeNummer && altOhneNummer) {
        return formatiereTelefonRoh(sauber, sauber.length, mitMuster);
    }
    const roh = alt.slice(0, start) + sauber + alt.slice(ende);
    return formatiereTelefonRoh(roh, start + sauber.length, mitMuster);
}
/**
 * Anzeige beim Verlassen des Feldes: Muster anwenden, Reste abschneiden.
 * Liefert den neuen Feldinhalt (und damit auch den Speicherwert).
 */
export function telefonVerlassen(wert, opts = {}) {
    const s = String(wert ?? "");
    if (istTelefonFreitext(s))
        return s.trim();
    return telefonSpeicherwert(formatiereTelefonRoh(s.trim(), s.trim().length, { ...opts, muster: true }).wert);
}
/** E.164 begrenzt Ländervorwahl + Rufnummer auf 15 Ziffern. */
const MAX_ZIFFERN = 15;
/**
 * Prüfung für die Fehlermeldung unter dem Feld. Leer ist gültig
 * (Pflichtfelder sind Sache des Formulars).
 */
export function pruefeTelefon(wert) {
    const s = String(wert ?? "").trim();
    if (!s)
        return undefined;
    if (istTelefonFreitext(s))
        return "Bitte nur die Rufnummer eingeben (ohne Text).";
    const z = zerlegeTokens(tokenisiere(s), { geschaeftlich: true });
    if (!z.cc)
        return "Unbekannte Ländervorwahl.";
    const durchwahl = z.durchwahlAb !== undefined ? z.national.length - z.durchwahlAb : 0;
    if (durchwahl > MAX_DURCHWAHL)
        return `Die Durchwahl darf höchstens ${MAX_DURCHWAHL} Stellen haben.`;
    const hauptnummer = z.durchwahlAb !== undefined ? z.national.slice(0, z.durchwahlAb) : z.national;
    if (z.cc.length + z.national.length > MAX_ZIFFERN + 5 || z.cc.length + hauptnummer.length > MAX_ZIFFERN) {
        return "Die Rufnummer ist zu lang.";
    }
    if (z.cc.length + hauptnummer.length < 7)
        return "Bitte eine vollständige Rufnummer eingeben (z. B. +49 221 12345).";
    return undefined;
}
/** Nur die Ziffern — für Vergleiche („ist das dieselbe Nummer?"). */
export const telefonZiffern = (wert) => String(wert ?? "").replace(/\D/g, "");
/** Ländervorwahl eines formatierten Werts („+43 664…" → „43"), sonst "". */
export function telefonLaendervorwahl(wert) {
    const s = String(wert ?? "").trim();
    if (!s || istTelefonFreitext(s))
        return "";
    return zerlegeTokens(tokenisiere(s), {}).cc;
}
/**
 * Ist `wert` eine deutsche Mobilnummer (015x, 016x, 017x) oder eine deutsche
 * Festnetznummer (bekannte Ortsnetzkennzahl der BNetzA)? Versteht gespeicherte
 * („+49 1516 8462144") wie rohe Schreibweisen („01516 8462144", „0049…").
 *
 * Keine Aussage (`undefined`) bei allem, was nicht eindeutig ist: ausländische
 * Nummern (keine verlässliche Tabelle), Sonderrufnummern (0800, 0180, 032 …),
 * Freitext und halb getippte Nummern. Ein falscher Hinweis wäre schlimmer als
 * keiner — der Nutzer soll nur dann „Tauschen" angeboten bekommen, wenn die
 * Nummer erkennbar im falschen Feld steht.
 */
export function rufnummernArt(wert) {
    const s = String(wert ?? "").trim();
    if (!s || istTelefonFreitext(s))
        return undefined;
    const formatiert = formatiereTelefon(s, { geschaeftlich: true });
    if (telefonLaendervorwahl(formatiert) !== "49")
        return undefined;
    // Durchwahl („-") gehört zur Rufnummer — für die Art zählen nur die Ziffern.
    const national = telefonZiffern(formatiert).slice(2);
    if (/^1[567]/.test(national)) {
        // Kürzeste deutsche Mobilnummer: 3-/4-stellige Vorwahl + 7 Ziffern.
        return national.length >= 10 ? "mobil" : undefined;
    }
    if (!/^[2-9]/.test(national))
        return undefined;
    // 0700/0800/0900 und 032 sind Sonderrufnummern, kein Festnetz.
    if (/^(700|800|900|32)/.test(national))
        return undefined;
    const vorwahl = deutscheVorwahlLaenge(national);
    if (vorwahl === undefined)
        return undefined;
    // Mindestens drei Ziffern Teilnehmernummer — sonst wird noch getippt.
    return national.length - vorwahl >= 3 ? "festnetz" : undefined;
}
export function telefonMobilHinweis(telefon, mobil) {
    const leer = (v) => !String(v ?? "").trim();
    const t = rufnummernArt(telefon);
    const m = rufnummernArt(mobil);
    if (t === "mobil") {
        return {
            feld: "telefon",
            art: "mobil",
            aktion: leer(mobil) ? "verschieben" : m === "festnetz" ? "tauschen" : undefined,
        };
    }
    if (m === "festnetz") {
        return {
            feld: "mobil",
            art: "festnetz",
            aktion: leer(telefon) ? "verschieben" : undefined,
        };
    }
    return null;
}
/**
 * Führt die Aktion eines {@link TelefonMobilHinweis} aus: Werte tauschen bzw.
 * die Nummer ins leere Nachbarfeld verschieben. Die Werte wandern UNVERÄNDERT
 * (gleiches Format, gleiche Durchwahl) — es ändert sich nur das Feld.
 * Ohne Aktion bleibt alles, wie es ist.
 */
export function telefonMobilAnwenden(hinweis, paar) {
    if (!hinweis?.aktion)
        return paar;
    if (hinweis.aktion === "tauschen")
        return { telefon: paar.mobil, mobil: paar.telefon };
    return hinweis.feld === "telefon"
        ? { telefon: "", mobil: paar.telefon }
        : { telefon: paar.mobil, mobil: "" };
}
